import { MOSIDA_API_BASE_URL } from './mosidaApi';
import { readAuthSession } from '../services/authSession';

/** Logged-in kiosk user id for payment QR / save-card association. */
export function resolvePaymentUserId(explicit) {
  const fromExplicit = explicit != null ? String(explicit).trim() : '';
  if (fromExplicit) return fromExplicit;
  return readAuthSession()?.user?.id ?? null;
}

function injectPaymentUserId(payload) {
  if (!payload || typeof payload !== 'object') return payload;

  const existing =
    payload.userId ??
    payload.user_id ??
    payload.data?.userId ??
    payload.data?.user_id ??
    null;
  const paymentUserId = resolvePaymentUserId(existing);
  if (!paymentUserId) return payload;

  if (payload.data && typeof payload.data === 'object') {
    return {
      ...payload,
      data: {
        ...payload.data,
        userId: paymentUserId,
        user_id: paymentUserId,
      },
    };
  }

  return {
    ...payload,
    userId: paymentUserId,
    user_id: paymentUserId,
  };
}

export function unwrapTempDataRecord(json) {
  if (!json || typeof json !== 'object') return {};
  if (json.id) return json;
  if (json.data && typeof json.data === 'object' && json.data.id) return json.data;
  if (json.data?.data?.id) return json.data.data;
  return json.data && typeof json.data === 'object' ? json.data : json;
}

export async function createTempData(payload) {
  try {
    const requestPayload = injectPaymentUserId(payload);
    const response = await fetch(`${MOSIDA_API_BASE_URL}/user/payment/temp-data`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestPayload),
    });
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText || `HTTP error! status: ${response.status}` };
      }
      return {
        success: false,
        error:
          errorData.message ||
          errorData.error ||
          `HTTP error! status: ${response.status}`,
      };
    }
    const json = await response.json();
    return { success: true, data: unwrapTempDataRecord(json) };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Network error while generating payment QR',
    };
  }
}

export async function scanQRPaymentAPI(id, payload) {
  try {
    const requestBody = injectPaymentUserId(payload);
    const response = await fetch(
      `${MOSIDA_API_BASE_URL}/user/payment/generate-payment-link/${id}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText || `HTTP error! status: ${response.status}` };
      }
      return {
        success: false,
        error:
          errorData.message ||
          errorData.error ||
          `HTTP error! status: ${response.status}`,
      };
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const responseData = await response.json();
      if (typeof responseData === 'string') {
        return { success: true, data: responseData };
      }
      if (responseData.data) {
        return {
          success: true,
          data: responseData.data,
          message: responseData.message,
        };
      }
      return { success: true, data: responseData, message: responseData.message };
    }

    const responseText = await response.text();
    if (responseText.startsWith('http://') || responseText.startsWith('https://')) {
      return { success: true, data: responseText.trim() };
    }
    try {
      const parsed = JSON.parse(responseText);
      return {
        success: true,
        data: parsed.data || parsed.qrCodeUrl || parsed.qrCode || parsed.paymentLink || parsed,
        message: parsed.message,
      };
    } catch {
      return { success: true, data: responseText };
    }
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Network error while generating payment QR',
    };
  }
}
